import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { format, addDays, startOfWeek, endOfWeek, parseISO, isToday, isBefore, subWeeks, addWeeks, eachDayOfInterval } from 'date-fns';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, collection as fbCol, query as fbQuery, where as fbWhere, getDocs as fbGetDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  MapPin, Clock, Phone, Navigation, ChevronRight, ChevronLeft, Play, CheckCircle,
  Camera, AlertTriangle, Car, User, Calendar, Home, FileText,
  Key, PawPrint, Ruler, Bath, BedDouble, X, Upload, Send,
  LogOut, Menu, Info, Timer, ChevronDown, ChevronUp,
  Wifi, WifiOff, Tag, Book, Loader, Image, Check, Sparkles,
  MessageSquare, Bell, ClipboardList, Trophy, Award, Medal, DollarSign, Square
} from 'lucide-react';
import { serviceTypes } from '../utils/jobData';
import { uploadJobPhoto, uploadMultiplePhotos, uploadDamageReport, checkRequiredPhotos } from '../utils/photoUpload';
import { notifyTeamOnTheWay, notifyJobCompleted, notifyManagers } from '../utils/notificationsApi';
import { quizzes as quizBank, MONTHLY_QUIZ_REQUIREMENT } from '../utils/quizData';
import EmployeeInbox from '../components/EmployeeInbox';
import TimeOffRequest from '../components/TimeOffRequest';
import { EmployeeSelfReview } from '../components/PerformanceReview';
import EmployeeAttendanceView from '../components/EmployeeAttendanceView';
import { subscribeChecklistItems } from '../modules/onboarding/services/onboardingService';
import {
  getApplicableChecklistItems,
  CHECKLIST_FREQUENCY_LABELS,
} from '../utils/checklistFrequency';
import '../components/EmployeeInbox.css';
import '../components/TimeOffRequest.css';
import '../components/EmployeeAttendanceView.css';
import './TeamApp.css';

// GPS ping interval (30 seconds)
const GPS_PING_INTERVAL = 30000;

// Schedule View Component
function ScheduleView({ currentEmployee, state, getEmployeeShifts, hasTimeOffOnDate }) {
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const days = Array.from({ length: 14 }, (_, i) => addDays(weekStart, i));
  const myShifts = getEmployeeShifts ? getEmployeeShifts(currentEmployee.id) : [];

  const getMyJobs = (dateStr) => {
    return state.jobs.filter(j => 
      j.date === dateStr && 
      j.status !== 'cancelled' &&
      j.team?.includes(currentEmployee.id)
    ).sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));
  };

  return (
    <div className="schedule-view">
      <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a2634', padding: '0 16px' }}>My Schedule</h3>
      <div className="schedule-week-list">
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const shift = myShifts.find(s => s.date === dateStr);
          const timeOff = hasTimeOffOnDate ? hasTimeOffOnDate(currentEmployee.id, dateStr) : false;
          const dayJobs = getMyJobs(dateStr);
          const dayIsPast = isBefore(day, new Date(now.getFullYear(), now.getMonth(), now.getDate())) && !isToday(day);
          const isTodayDate = isToday(day);

          return (
            <div 
              key={dateStr} 
              className={`schedule-day-card ${isTodayDate ? 'today' : ''} ${dayIsPast ? 'past' : ''} ${timeOff ? 'time-off' : ''}`}
            >
              <div className="schedule-day-header">
                <div className="schedule-day-name">
                  <span className="day-label">{format(day, 'EEE')}</span>
                  <span className="day-date">{format(day, 'MMM d')}</span>
                  {isTodayDate && <span className="today-badge">Today</span>}
                </div>
                {timeOff ? (
                  <span className="schedule-badge off">Time Off</span>
                ) : shift ? (
                  <span className="schedule-badge shift">
                    {shift.startTime} - {shift.endTime}
                  </span>
                ) : dayJobs.length > 0 ? (
                  <span className="schedule-badge shift">Working</span>
                ) : (
                  <span className="schedule-badge none">Off</span>
                )}
              </div>
              {dayJobs.length > 0 && (
                <div className="schedule-day-jobs">
                  {dayJobs.map(job => (
                    <div key={job.id} className="schedule-job-item">
                      <span className="schedule-job-time">{job.scheduledTime || '—'}</span>
                      <span className="schedule-job-client">{job.clientName}</span>
                      {job.duration && <span className="schedule-job-dur">{job.duration}hr</span>}
                    </div>
                  ))}
                </div>
              )}
              {!timeOff && shift && dayJobs.length === 0 && (
                <div className="schedule-day-jobs">
                  <div className="schedule-job-item empty">
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>No jobs assigned yet</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamApp() {
  const { state, updateJob, addJob, updateJobChecklistItem, getEmployeeUnreadInternalCount, getPropertyById, updateProperty, getEmployeeShifts, hasTimeOffOnDate, getPendingEmployeeReviews, submitQuizAttempt, getEmployeeQuizAttempts } = useApp();
  const { user, logout, updateClockedInStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isViewingAsCleaner = searchParams.get('preview') === 'cleaner';

  const handleBackToManager = () => {
    localStorage.removeItem('viewAsCleaner');
    navigate('/admin/dashboard');
  };
  const [activeJob, setActiveJob] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoType, setPhotoType] = useState('before');
  const [showDamageModal, setShowDamageModal] = useState(false);
  const [damageType, setDamageType] = useState('existing');
  const [damageDescription, setDamageDescription] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('connecting'); // connecting, active, error
  const gpsIntervalRef = useRef(null);
  
  // View state: 'jobs' or 'messages'
  const [currentView, setCurrentView] = useState('jobs');
  
  // Photo upload state
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [photoJobId, setPhotoJobId] = useState(null);
  const photoInputRef = useRef(null);
  const damagePhotoInputRef = useRef(null);
  const [damagePhotos, setDamagePhotos] = useState([]);
  const propertyPhotoRef = useRef(null);
  const [uploadingPropertyPhoto, setUploadingPropertyPhoto] = useState(false);
  
  // Leaderboard state
  const [lbView, setLbView] = useState('week');
  
  // Notification state
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [notificationSent, setNotificationSent] = useState({});
  
  // Laundry pickup state
  const [showLaundryModal, setShowLaundryModal] = useState(false);
  const [laundryData, setLaundryData] = useState({
    weight: '',
    bagCount: 1,
    bagDescription: '',
    bagTags: ''
  });
  
  // Handbook state
  const [showHandbook, setShowHandbook] = useState(false);
  
  // Time off request state
  const [showTimeOffRequest, setShowTimeOffRequest] = useState(false);

  // Quiz state
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const today = format(new Date(), 'yyyy-MM-dd');

  // My Hours view state
  const [myHoursWeek, setMyHoursWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [myHoursRecords, setMyHoursRecords] = useState([]);
  const [myHoursLoading, setMyHoursLoading] = useState(false);

  // Daily clock in/out state
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [showClockModal, setShowClockModal] = useState(false);
  const [selectedPayRate, setSelectedPayRate] = useState(null);
  const [clockedInRole, setClockedInRole] = useState(null);

  // Get current employee from auth (matched by email)
  const currentEmployee = useMemo(() => {
    const employee = state.employees.find(emp => emp.email?.toLowerCase() === user?.email?.toLowerCase());
    return employee || null;
  }, [state.employees, user]);

  // Track outstanding onboarding checklist items so we can surface a link to
  // /onboarding when the cleaner has work to do (the team app has no other
  // entry point to the checklist).
  const [onboardingOpenCount, setOnboardingOpenCount] = useState(0);
  useEffect(() => {
    if (!currentEmployee?.id) {
      setOnboardingOpenCount(0);
      return undefined;
    }
    const unsub = subscribeChecklistItems(currentEmployee.id, (rows) => {
      const open = rows.filter((r) => r.status !== 'completed' && r.driver !== 'manager').length;
      setOnboardingOpenCount(open);
    });
    return () => unsub();
  }, [currentEmployee?.id]);

  // Check for existing clock-in on load (localStorage first, then Firestore fallback)
  useEffect(() => {
    if (currentEmployee?.id) {
      const savedClockIn = localStorage.getItem(`clockIn_${currentEmployee.id}_${today}`);
      if (savedClockIn) {
        setClockedIn(true);
        setClockInTime(savedClockIn);
        const savedRole = localStorage.getItem(`clockInRole_${currentEmployee.id}_${today}`);
        if (savedRole) {
          try { setClockedInRole(JSON.parse(savedRole)); } catch(e) {}
        }
        // Sync with AuthContext
        if (updateClockedInStatus) {
          updateClockedInStatus(true);
        }
      } else {
        // localStorage missing — check Firestore for an active clock-in
        const checkFirestore = async () => {
          try {
            const docId = `${currentEmployee.id}_${today}`;
            const snap = await getDoc(doc(db, 'timeRecords', docId));
            if (snap.exists() && snap.data().status === 'clocked_in') {
              const data = snap.data();
              // Restore state from Firestore
              setClockedIn(true);
              setClockInTime(data.clockInTime);
              if (data.clockInRole) {
                const roleInfo = { label: data.clockInRole, rate: data.clockInRate };
                setClockedInRole(roleInfo);
                localStorage.setItem(`clockInRole_${currentEmployee.id}_${today}`, JSON.stringify(roleInfo));
              }
              // Re-save to localStorage so future checks are fast
              localStorage.setItem(`clockIn_${currentEmployee.id}_${today}`, data.clockInTime);
              if (updateClockedInStatus) {
                updateClockedInStatus(true);
              }
            } else {
              if (updateClockedInStatus) {
                updateClockedInStatus(false);
              }
            }
          } catch (error) {
            console.error('Error checking Firestore for clock-in:', error);
            if (updateClockedInStatus) {
              updateClockedInStatus(false);
            }
          }
        };
        checkFirestore();
      }
    }
  }, [currentEmployee, today, updateClockedInStatus]);

  // Get today's jobs assigned to this employee
  const myJobs = useMemo(() => {
    if (!currentEmployee) return [];
    return state.jobs
      .filter(job => 
        job.date === today && 
        job.team?.includes(currentEmployee.id)
      )
      .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
  }, [state.jobs, today, currentEmployee]);

  // MY TEAMMATES TODAY — collect unique team members from all my jobs
  const myTeammates = useMemo(() => {
    if (!currentEmployee) return [];
    const teammateIds = new Set();
    myJobs.forEach(job => {
      (job.team || []).forEach(id => {
        if (id !== currentEmployee.id) teammateIds.add(id);
      });
    });
    return [...teammateIds].map(id => {
      const emp = state.employees.find(e => e.id === id);
      return emp || { id, firstName: 'Unknown', lastName: '' };
    });
  }, [myJobs, currentEmployee, state.employees]);

  // WEEKLY HOURS (Sun-Sat) — sum job durations for this week
  // Fetch actual time records for this week from Firestore
  const [weeklyTimeRecords, setWeeklyTimeRecords] = useState([]);
  useEffect(() => {
    if (!currentEmployee) return;
    const fetchTimeRecords = async () => {
      try {
        const now = new Date();
        const dayOfWeek = now.getDay();
        const weekSunday = addDays(now, -dayOfWeek);
        const dates = [];
        for (let i = 0; i < 7; i++) {
          dates.push(format(addDays(weekSunday, i), 'yyyy-MM-dd'));
        }
        const q = fbQuery(
          fbCol(db, 'timeRecords'),
          fbWhere('employeeId', '==', currentEmployee.id),
          fbWhere('date', '>=', dates[0]),
          fbWhere('date', '<=', dates[6])
        );
        const snap = await fbGetDocs(q);
        const records = [];
        snap.forEach(d => records.push({ id: d.id, ...d.data() }));
        setWeeklyTimeRecords(records);
      } catch (e) {
        console.error('Error fetching time records:', e);
      }
    };
    fetchTimeRecords();
  }, [currentEmployee, today]);

  const weeklyHours = useMemo(() => {
    if (!currentEmployee) return { total: 0, days: [] };
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=Sun
    const weekSunday = addDays(now, -dayOfWeek);
    const days = [];
    let total = 0;
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekSunday, i);
      const dateStr = format(d, 'yyyy-MM-dd');
      // Use actual time records if available, fall back to job durations
      const timeRecord = weeklyTimeRecords.find(r => r.date === dateStr);
      let hrs = 0;
      if (timeRecord?.hoursWorked) {
        hrs = Math.round(timeRecord.hoursWorked * 10) / 10;
      } else if (timeRecord?.clockInTime && !timeRecord.clockOutTime) {
        // Currently clocked in — calculate live hours
        hrs = Math.round(((new Date() - new Date(timeRecord.clockInTime)) / 3600000) * 10) / 10;
      }
      total += hrs;
      days.push({ label: format(d, 'EEE'), date: dateStr, hrs, isToday: dateStr === today, clockedIn: timeRecord?.status === 'clocked_in' });
    }
    total = Math.round(total * 10) / 10;
    return { total, days };
  }, [weeklyTimeRecords, currentEmployee, today]);

  // ===== MY HOURS VIEW: Fetch time records for selected week =====
  useEffect(() => {
    if (!currentEmployee || currentView !== 'myhours') return;
    const loadMyHoursRecords = async () => {
      setMyHoursLoading(true);
      try {
        const weekEnd = endOfWeek(myHoursWeek, { weekStartsOn: 1 });
        const startStr = format(myHoursWeek, 'yyyy-MM-dd');
        const endStr = format(weekEnd, 'yyyy-MM-dd');
        const q = fbQuery(
          fbCol(db, 'timeRecords'),
          fbWhere('employeeId', '==', currentEmployee.id),
          fbWhere('date', '>=', startStr),
          fbWhere('date', '<=', endStr)
        );
        const snap = await fbGetDocs(q);
        const records = [];
        snap.forEach(d => records.push({ id: d.id, ...d.data() }));
        setMyHoursRecords(records);
      } catch (e) {
        console.error('Error loading my hours records:', e);
      } finally {
        setMyHoursLoading(false);
      }
    };
    loadMyHoursRecords();
  }, [currentEmployee, myHoursWeek, currentView]);

  const myHoursData = useMemo(() => {
    if (!currentEmployee) return { days: [], totalHours: 0 };
    const weekEnd = endOfWeek(myHoursWeek, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: myHoursWeek, end: weekEnd });

    const dayData = days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const record = myHoursRecords.find(r => r.date === dateStr);
      let hrs = 0;
      let clockIn = null;
      let clockOut = null;
      let rate = null;
      let role = null;

      if (record) {
        hrs = record.hoursWorked || 0;
        clockIn = record.clockInTime || null;
        clockOut = record.clockOutTime || null;
        rate = record.clockInRate || currentEmployee.hourlyRate || null;
        role = record.clockInRole || null;

        // If currently clocked in and no hours yet, calculate live
        if (record.clockInTime && !record.clockOutTime && !record.hoursWorked) {
          hrs = Math.round(((new Date() - new Date(record.clockInTime)) / 3600000) * 100) / 100;
        }
      }

      return {
        date: dateStr,
        dayName: format(day, 'EEE'),
        dayDate: format(day, 'MMM d'),
        hrs: Math.round(hrs * 100) / 100,
        clockIn,
        clockOut,
        rate,
        role,
        isToday: dateStr === today,
        isClockedIn: record?.status === 'clocked_in'
      };
    });

    const totalHours = Math.round(dayData.reduce((sum, d) => sum + d.hrs, 0) * 100) / 100;
    return { days: dayData, totalHours };
  }, [myHoursRecords, myHoursWeek, currentEmployee, today]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalJobs = myJobs.length;
    const completed = myJobs.filter(j => j.status === 'completed').length;
    const inProgress = myJobs.filter(j => j.status === 'in_progress').length;
    const totalHours = myJobs.reduce((sum, j) => sum + (j.duration || 0), 0);
    const totalRevenue = myJobs.reduce((sum, j) => sum + (j.price || 0), 0);
    return { totalJobs, completed, inProgress, totalHours, totalRevenue };
  }, [myJobs]);

  // === LEADERBOARD (profit-ranked, matches Dashboard — no dollar amounts shown) ===
  const [lbTimeRecords, setLbTimeRecords] = useState([]);
  useEffect(() => {
    const loadLbTimeRecords = async () => {
      try {
        const now = new Date();
        const wStart = startOfWeek(now, { weekStartsOn: 1 });
        const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const weekStr = format(wStart, 'yyyy-MM-dd');
        const monthStr = format(mStart, 'yyyy-MM-dd');
        const rangeStart = weekStr < monthStr ? weekStr : monthStr;
        const rangeEnd = format(now, 'yyyy-MM-dd');
        const q = fbQuery(
          fbCol(db, 'timeRecords'),
          fbWhere('date', '>=', rangeStart),
          fbWhere('date', '<=', rangeEnd)
        );
        const snap = await fbGetDocs(q);
        const records = [];
        snap.forEach(d => records.push({ id: d.id, ...d.data() }));
        setLbTimeRecords(records);
      } catch (e) {
        console.error('Error loading leaderboard time records:', e);
      }
    };
    loadLbTimeRecords();
  }, [today]);

  const leaderboard = useMemo(() => {
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    const wStart = startOfWeek(now, { weekStartsOn: 1 });
    
    // Get date ranges (same as Dashboard)
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const d = format(addDays(wStart, i), 'yyyy-MM-dd');
      if (d < todayStr) weekDates.push(d);
    }
    
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthDates = [];
    let cursor = new Date(mStart);
    while (cursor.getMonth() === mStart.getMonth()) {
      const d = format(cursor, 'yyyy-MM-dd');
      if (d < todayStr) monthDates.push(d);
      cursor = addDays(cursor, 1);
    }

    // Build employee rate lookup
    const empRates = {};
    state.employees.forEach(emp => {
      empRates[emp.id] = { hourlyRate: emp.hourlyRate || 15, name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(), firstName: emp.firstName, lastInitial: emp.lastName?.charAt(0) || '' };
    });

    // Same profit calculation as Dashboard
    const calcRanking = (dateList) => {
      const profitByPerson = {};

      dateList.forEach(dateStr => {
        const dayJobs = state.jobs.filter(j => j.date === dateStr);

        const jobEntries = dayJobs.map(job => {
          const jobTeam = [...new Set([...(job.team || []), ...(job.helpers || [])])];
          return { job, jobTeam };
        });

        const allTodayEmpIds = [...new Set(jobEntries.flatMap(e => e.jobTeam))];
        const dayRecords = lbTimeRecords.filter(r => r.date === dateStr && allTodayEmpIds.includes(r.employeeId));
        const clockedHours = {};
        dayRecords.forEach(r => { if (r.hoursWorked) clockedHours[r.employeeId] = r.hoursWorked; });

        jobEntries.forEach(({ job, jobTeam }) => {
          if (jobTeam.length === 0) return;

          const jobRevenue = job.price || 0;
          const jobHours = job.duration || 0;
          const driveHours = 0.25;

          let jobLaborCost = 0;
          if (Object.keys(clockedHours).length > 0) {
            jobTeam.forEach(empId => {
              const rate = empRates[empId]?.hourlyRate || 15;
              jobLaborCost += (jobHours + driveHours) * rate;
            });
          } else {
            const avgRate = jobTeam.reduce((s, id) => s + (empRates[id]?.hourlyRate || 15), 0) / jobTeam.length;
            jobLaborCost = (jobHours + driveHours) * avgRate * jobTeam.length;
          }

          const jobProfit = jobRevenue - jobLaborCost;
          const profitPerPerson = jobProfit / jobTeam.length;

          jobTeam.forEach(empId => {
            if (!profitByPerson[empId]) {
              profitByPerson[empId] = { empId, name: empRates[empId] ? `${empRates[empId].name.split(' ')[0]} ${empRates[empId].lastInitial}.` : 'Unknown', firstName: empRates[empId]?.firstName || '?', totalProfit: 0, jobs: 0, routeCount: 0, days: new Set() };
            }
            profitByPerson[empId].totalProfit += profitPerPerson;
            profitByPerson[empId].jobs += 1;
            profitByPerson[empId].days.add(dateStr);
          });
        });

        // Count unique routes per person
        const routesByPerson = {};
        dayJobs.forEach(job => {
          const jobTeam = [...new Set([...(job.team || []), ...(job.helpers || [])])];
          jobTeam.forEach(empId => {
            if (!routesByPerson[empId]) routesByPerson[empId] = new Set();
            routesByPerson[empId].add(job.routeId || 'unassigned');
          });
        });
        Object.entries(routesByPerson).forEach(([empId, routeSet]) => {
          if (profitByPerson[empId]) profitByPerson[empId].routeCount += routeSet.size;
        });
      });

      return Object.values(profitByPerson)
        .map(p => ({ ...p, daysWorked: p.days.size }))
        .sort((a, b) => b.totalProfit - a.totalProfit);
    };

    return {
      week: calcRanking(weekDates),
      month: calcRanking(monthDates),
      weekLabel: weekDates.length > 0 ? `${format(parseISO(weekDates[0]), 'MMM d')} – ${format(parseISO(weekDates[weekDates.length - 1]), 'MMM d')}` : 'No data yet',
      monthLabel: format(mStart, 'MMMM yyyy')
    };
  }, [state.jobs, state.employees, lbTimeRecords]);

  // === PROPERTY PHOTO UPLOAD ===
  const handlePropertyPhotoUpload = async (e, job) => {
    const file = e.target.files?.[0];
    if (!file || !job) return;
    
    const propertyId = job.propertyId;
    const clientId = job.clientId;
    if (!propertyId && !clientId) {
      alert('No property linked to this job.');
      return;
    }
    
    setUploadingPropertyPhoto(true);
    
    try {
      // Compress and upload to Firebase Storage
      const timestamp = Date.now();
      const { ref: storageRef, uploadBytes: ub, getDownloadURL: gdl } = await import('firebase/storage');
      const { storage } = await import('../firebase');
      
      // Compress
      const compressedBlob = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (ev) => {
          const img = new window.Image();
          img.src = ev.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let w = img.width, h = img.height;
            if (w > 1200) { h = Math.round(h * 1200 / w); w = 1200; }
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
          };
          img.onerror = reject;
        };
        reader.onerror = reject;
      });
      
      const targetId = propertyId || clientId;
      const path = `properties/${targetId}/property_photo_${timestamp}.jpg`;
      const sRef = storageRef(storage, path);
      const snap = await ub(sRef, compressedBlob, { contentType: 'image/jpeg' });
      const downloadURL = await gdl(snap.ref);
      
      // Save to property document
      if (propertyId) {
        await updateProperty(propertyId, { photoUrl: downloadURL });
      }
      // Also save on the job for quick access
      await updateJob(job.id, { propertyPhotoUrl: downloadURL });
      
      alert('🏠 Property photo saved!');
    } catch (err) {
      console.error('Property photo upload error:', err);
      alert('Failed to upload property photo. Please try again.');
    } finally {
      setUploadingPropertyPhoto(false);
      if (propertyPhotoRef.current) propertyPhotoRef.current.value = '';
    }
  };

  // Daily Clock In
  const handleDailyClockIn = async () => {
    // If employee has multiple pay rates and none selected, don't proceed
    const hasMultipleRates = currentEmployee?.payRates?.length > 0;
    if (hasMultipleRates && !selectedPayRate) return;

    const now = new Date();

    // Save to localStorage
    localStorage.setItem(`clockIn_${currentEmployee.id}_${today}`, now.toISOString());
    setClockedIn(true);
    setClockInTime(now.toISOString());

    // Save selected role info
    const roleInfo = selectedPayRate || null;
    if (roleInfo) {
      localStorage.setItem(`clockInRole_${currentEmployee.id}_${today}`, JSON.stringify(roleInfo));
      setClockedInRole(roleInfo);
    }

    // Notify AuthContext (prevents idle logout while clocked in)
    if (updateClockedInStatus) {
      updateClockedInStatus(true);
    }

    // Save to Firestore - single document per employee per day
    try {
      const docId = `${currentEmployee.id}_${today}`;
      const timeRecord = {
        employeeId: currentEmployee.id,
        employeeName: `${currentEmployee.firstName} ${currentEmployee.lastName}`,
        date: today,
        clockInTime: now.toISOString(),
        clockInLocation: currentLocation ? {
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          accuracy: currentLocation.accuracy
        } : null,
        status: 'clocked_in',
        approvalStatus: 'pending',
        updatedAt: serverTimestamp()
      };
      if (roleInfo) {
        timeRecord.clockInRole = roleInfo.label;
        timeRecord.clockInRate = roleInfo.rate;
      }
      await setDoc(doc(db, 'timeRecords', docId), timeRecord, { merge: true });
    } catch (error) {
      console.error('Error saving clock in:', error);
    }

    setShowClockModal(false);
    setSelectedPayRate(null);
  };

  // Daily Clock Out
  const handleDailyClockOut = async () => {
    const now = new Date();
    const clockInDate = new Date(clockInTime);
    const hoursWorked = ((now - clockInDate) / 3600000).toFixed(2);
    
    // Clear localStorage
    localStorage.removeItem(`clockIn_${currentEmployee.id}_${today}`);
    localStorage.removeItem(`clockInRole_${currentEmployee.id}_${today}`);
    setClockedIn(false);
    setClockedInRole(null);
    
    // Notify AuthContext (re-enables idle logout)
    if (updateClockedInStatus) {
      updateClockedInStatus(false);
    }
    
    // Save to Firestore
    try {
      const docId = `${currentEmployee.id}_${today}`;
      await setDoc(doc(db, 'timeRecords', docId), {
        clockOutTime: now.toISOString(),
        clockOutLocation: currentLocation ? {
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          accuracy: currentLocation.accuracy
        } : null,
        hoursWorked: parseFloat(hoursWorked),
        status: 'clocked_out',
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving clock out:', error);
    }

    // Remove GPS location so employee disappears from live tracking
    try {
      await deleteDoc(doc(db, 'gps_locations', currentEmployee.id));
    } catch (error) {
      console.error('Error removing GPS location on clock out:', error);
    }
    
    setShowClockModal(false);
  };

  // Calculate hours worked so far
  const getHoursWorked = () => {
    if (!clockInTime) return '0:00';
    const now = new Date();
    const start = new Date(clockInTime);
    const diff = now - start;
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // Send GPS ping to Firestore
  const sendGpsPing = async (position) => {
    if (!currentEmployee?.id || !user) return;
    
    try {
      const activeJobData = myJobs.find(j => j.status === 'in_progress');
      
      await setDoc(doc(db, 'gps_locations', currentEmployee.id), {
        empId: currentEmployee.id,
        empName: `${currentEmployee.firstName || ''} ${currentEmployee.lastName || ''}`.trim() || user.email,
        empEmail: user.email,
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString(),
        serverTimestamp: serverTimestamp(),
        currentJobId: activeJobData?.id || null,
        currentJobClient: activeJobData?.clientName || null,
        currentJobAddress: activeJobData?.address || null,
        status: activeJobData ? 'working' : (myJobs.some(j => j.status === 'en_route') ? 'en_route' : 'available'),
        batteryLevel: navigator.getBattery ? await navigator.getBattery().then(b => Math.round(b.level * 100)) : null
      });
      
      setGpsStatus('active');
    } catch (error) {
      console.error('Error sending GPS ping:', error);
      setGpsStatus('error');
    }
  };

  // Get GPS location and set up continuous tracking (only while clocked in)
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      setGpsStatus('error');
      return;
    }

    // Only track and send GPS pings while clocked in
    if (!clockedIn) {
      setGpsStatus('inactive');
      return;
    }

    let watchId;

    // Watch position for real-time updates
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLocationError(null);
        setGpsStatus('active');

        // Send initial ping
        sendGpsPing(position);
      },
      (error) => {
        setLocationError(error.message);
        setGpsStatus('error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );

    // Set up interval for regular GPS pings (every 30 seconds)
    gpsIntervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          sendGpsPing(position);
        },
        (error) => {
          console.error('GPS ping error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
      );
    }, GPS_PING_INTERVAL);

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
    };
  }, [currentEmployee?.id, user, myJobs, clockedIn]);

  // Check if within geofence (150m default)
  const isWithinGeofence = (jobLat, jobLng, radius = 150) => {
    if (!currentLocation) return null;
    const R = 6371e3; // Earth's radius in meters
    const φ1 = currentLocation.lat * Math.PI / 180;
    const φ2 = jobLat * Math.PI / 180;
    const Δφ = (jobLat - currentLocation.lat) * Math.PI / 180;
    const Δλ = (jobLng - currentLocation.lng) * Math.PI / 180;
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance <= radius;
  };

  // Mark On My Way - sends notification to client
  const handleOnMyWay = async (job) => {
    setIsSendingNotification(true);
    
    try {
      // Update job status first
      await updateJob(job.id, {
        status: 'en_route',
        enRouteAt: new Date().toISOString(),
        enRouteLocation: currentLocation
      });
      
      // Send notification to client (respects their preferences)
      if (job.clientId) {
        try {
          // Build a minimal client object for the notification system
          const clientForNotification = {
            id: job.clientId,
            firstName: job.clientName?.split(' ')[0] || '',
            lastName: job.clientName?.split(' ').slice(1).join(' ') || '',
            email: job.clientEmail,
            phone: job.clientPhone,
            notificationPrefs: job.clientNotificationPrefs,
            textConsent: job.clientTextConsent
          };
          
          await notifyTeamOnTheWay({
            client: clientForNotification,
            teamName: currentEmployee ? `${currentEmployee.firstName}` : 'Your team',
            eta: job.scheduledTime,
            address: job.address,
            portalLink: 'https://keelgroup.app/portal'
          });
          
          // Mark notification as sent for this job
          setNotificationSent(prev => ({ ...prev, [job.id]: 'on_my_way' }));
          
          // Update job with notification status
          await updateJob(job.id, {
            clientNotifiedOnMyWay: true,
            clientNotifiedOnMyWayAt: new Date().toISOString()
          });
        } catch (notifError) {
          console.error('Failed to send On My Way notification:', notifError);
          // Don't fail the whole operation if notification fails
        }
      }
    } catch (error) {
      console.error('Error marking on my way:', error);
      alert('Error updating status. Please try again.');
    } finally {
      setIsSendingNotification(false);
    }
  };

  // Start Job (Clock In) - REQUIRES before photos
  const handleStartJob = async (job) => {
    // Check for before photos first
    const photoStatus = checkRequiredPhotos(job);
    
    if (!photoStatus.hasBefore) {
      alert('📸 Before photos required!\n\nPlease take at least one "before" photo to start this job.');
      openPhotoModal(job.id, 'before');
      return;
    }
    
    const withinGeofence = isWithinGeofence(job.lat, job.lng);
    
    if (withinGeofence === false) {
      const proceed = window.confirm(
        'You appear to be outside the job location. Clock in anyway?\n\nThis will be flagged for manager review.'
      );
      if (!proceed) return;
    }

    await updateJob(job.id, {
      status: 'in_progress',
      actual_start: new Date().toISOString(),
      clockInLocation: currentLocation,
      geofenceVerified: withinGeofence === true
    });
    setActiveJob(job.id);
  };

  // Complete Job (Clock Out) - REQUIRES after photos
  const handleCompleteJob = async (job) => {
    // For laundry pickups, show the laundry modal first
    if (job.serviceType === 'laundry_pickup') {
      setShowLaundryModal(true);
      return;
    }
    
    // Check if required photos are taken
    const photoStatus = checkRequiredPhotos(job);
    
    if (!photoStatus.hasBefore) {
      alert('📸 Before photos required!\n\nPlease take "before" photos before completing.');
      openPhotoModal(job.id, 'before');
      return;
    }
    
    if (!photoStatus.hasAfter) {
      alert('📸 After photos required!\n\nPlease take at least one "after" photo to complete this job.');
      openPhotoModal(job.id, 'after');
      return;
    }
    
    // Final confirmation
    const proceed = window.confirm('Mark this job as complete?');
    if (!proceed) return;

    await completeJobFinal(job, {});
  };
  
  // Complete laundry job with weight data
  const handleCompleteLaundryJob = async () => {
    const job = myJobs.find(j => j.id === activeJob);
    if (!job) return;
    
    if (!laundryData.weight || parseFloat(laundryData.weight) <= 0) {
      alert('Please enter the laundry weight');
      return;
    }
    
    await completeJobFinal(job, {
      laundryWeight: parseFloat(laundryData.weight),
      laundryBagCount: laundryData.bagCount,
      laundryBagDescription: laundryData.bagDescription,
      laundryBagTags: laundryData.bagTags,
      laundryPrice: parseFloat(laundryData.weight) * 3.00 // $3/lb
    });
    
    setShowLaundryModal(false);
    setLaundryData({ weight: '', bagCount: 1, bagDescription: '', bagTags: '' });
  };
  
  // Final job completion with labor cost calculation
  const completeJobFinal = async (job, additionalData) => {
    const startTime = new Date(job.actual_start);
    const endTime = new Date();
    const durationMin = Math.round((endTime - startTime) / 60000);
    const durationHours = durationMin / 60;

    // Calculate labor cost based on team pay rates
    let laborCost = 0;
    if (job.team && job.team.length > 0) {
      job.team.forEach(empId => {
        const employee = state.employees.find(e => e.id === empId);
        if (employee?.payRate) {
          laborCost += durationHours * employee.payRate;
        }
      });
    }
    
    // Round labor cost to cents
    laborCost = Math.round(laborCost * 100) / 100;
    
    // Calculate profit margin
    const revenue = job.price || 0;
    const profitMargin = revenue - laborCost;
    const laborPercent = revenue > 0 ? Math.round((laborCost / revenue) * 100) : 0;

    await updateJob(job.id, {
      status: 'completed',
      actual_end: endTime.toISOString(),
      actual_duration_min: durationMin,
      actual_duration_hours: Math.round(durationHours * 100) / 100,
      clockOutLocation: currentLocation,
      completedAt: endTime.toISOString(),
      laborCost: laborCost,
      profitMargin: Math.round(profitMargin * 100) / 100,
      laborPercent: laborPercent,
      ...additionalData
    });
    
    // Send job completed notification to client (respects their preferences)
    if (job.clientId) {
      try {
        const clientForNotification = {
          id: job.clientId,
          firstName: job.clientName?.split(' ')[0] || '',
          lastName: job.clientName?.split(' ').slice(1).join(' ') || '',
          email: job.clientEmail,
          phone: job.clientPhone,
          notificationPrefs: job.clientNotificationPrefs,
          textConsent: job.clientTextConsent
        };
        
        await notifyJobCompleted({
          client: clientForNotification,
          address: job.address
        });
        
        await updateJob(job.id, {
          clientNotifiedCompleted: true,
          clientNotifiedCompletedAt: new Date().toISOString()
        });
      } catch (notifError) {
        console.error('Failed to send job completed notification:', notifError);
      }
    }
    
    setActiveJob(null);
  };

  // Report Damage with photo upload
  const handleReportDamage = async () => {
    if (!damageDescription.trim()) {
      alert('Please describe the damage');
      return;
    }
    
    if (damagePhotos.length === 0) {
      alert('Please take at least one photo of the damage');
      return;
    }
    
    const job = myJobs.find(j => j.id === activeJob);
    if (!job) return;
    
    setIsUploading(true);
    setUploadProgress('Uploading damage report...');
    
    try {
      // Upload damage report with photos
      const result = await uploadDamageReport({
        jobId: job.id,
        employeeId: currentEmployee.id,
        damageType: damageType,
        description: damageDescription,
        photos: damagePhotos
      });
      
      // Notify managers
      await notifyManagers({
        type: damageType === 'new' ? 'new_damage' : 'existing_damage',
        jobId: job.id,
        jobAddress: job.address,
        clientName: job.clientName,
        teamMemberName: `${currentEmployee.firstName} ${currentEmployee.lastName}`,
        description: damageDescription,
        photos: result.damageReport.photos
      });
      
      setShowDamageModal(false);
      setDamageDescription('');
      setDamagePhotos([]);
      alert(damageType === 'new' 
        ? 'Damage report submitted. Manager has been notified immediately.' 
        : 'Pre-existing damage documented.');
    } catch (error) {
      console.error('Error submitting damage report:', error);
      alert('Error submitting damage report. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // Open photo modal for a specific job
  const openPhotoModal = (jobId, type) => {
    setPhotoJobId(jobId);
    setPhotoType(type);
    setSelectedPhotos([]);
    setShowPhotoModal(true);
  };

  // Handle photo selection from camera/gallery
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedPhotos(prev => [...prev, ...files]);
    }
  };

  // Handle damage photo selection
  const handleDamagePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setDamagePhotos(prev => [...prev, ...files]);
    }
  };

  // Remove selected photo
  const removeSelectedPhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Remove damage photo
  const removeDamagePhoto = (index) => {
    setDamagePhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Upload photos to Firebase Storage
  const handlePhotoUpload = async () => {
    if (selectedPhotos.length === 0) {
      alert('Please select at least one photo');
      return;
    }
    
    if (!photoJobId) {
      alert('Error: No job selected');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(`Uploading ${selectedPhotos.length} photo(s)...`);
    
    try {
      const result = await uploadMultiplePhotos(
        selectedPhotos,
        photoJobId,
        photoType,
        currentEmployee.id
      );
      
      if (result.errors.length > 0) {
        console.error('Some photos failed to upload:', result.errors);
        alert(`${result.uploaded.length} photos uploaded. ${result.errors.length} failed.`);
      } else {
        alert(`${result.uploaded.length} ${photoType} photo(s) uploaded successfully!`);
      }
      
      setShowPhotoModal(false);
      setSelectedPhotos([]);
      setPhotoJobId(null);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // Get photo counts for a job
  const getJobPhotoStatus = (job) => {
    return checkRequiredPhotos(job);
  };

  // Open in Google Maps
  const openNavigation = (address) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#16a34a';
      case 'in_progress': return '#2563eb';
      case 'en_route': return '#d97706';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'en_route': return 'On My Way';
      default: return 'Scheduled';
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // No employee match - show error
  if (!currentEmployee) {
    return (
      <div className="team-app">
        {isViewingAsCleaner && (
          <div className="manager-view-banner">
            <span>You're viewing as a Cleaner</span>
            <button className="banner-back-btn" onClick={handleBackToManager}>
              <ChevronLeft size={16} /> Back to Manager View
            </button>
          </div>
        )}
        <div className="employee-selector-modal">
          <div className="selector-content">
            <div className="selector-header">
              <AlertTriangle size={48} style={{ color: '#dc2626' }} />
              <h2>Account Not Linked</h2>
              <p>Your login email <strong>{user?.email}</strong> doesn't match any employee record.</p>
            </div>
            <div className="no-employees">
              <p>Please contact your manager to:</p>
              <ol style={{ textAlign: 'left', marginTop: '12px' }}>
                <li>Add your email to your employee profile, OR</li>
                <li>Create a Firebase account with your work email</li>
              </ol>
              {isViewingAsCleaner ? (
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: '20px' }}
                  onClick={handleBackToManager}
                >
                  <ChevronLeft size={18} /> Back to Manager View
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: '20px' }}
                  onClick={() => logout()}
                >
                  <LogOut size={18} /> Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-app">
      {isViewingAsCleaner && (
        <div className="manager-view-banner">
          <span>You're viewing as a Cleaner</span>
          <button className="banner-back-btn" onClick={handleBackToManager}>
            <ChevronLeft size={16} /> Back to Manager View
          </button>
        </div>
      )}
      {/* Header */}
      <header className="team-header">
        <div className="header-left">
          <div className="team-avatar">
            {currentEmployee?.firstName?.[0]}{currentEmployee?.lastName?.[0]}
          </div>
          <div className="header-info">
            <span className="greeting">Good {new Date().getHours() < 12 ? 'morning' : 'afternoon'},</span>
            <span className="team-name">{currentEmployee?.firstName}</span>
          </div>
        </div>
        <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>
          <Menu size={24} />
        </button>
        
        {showMenu && (
          <div className="dropdown-menu">
            <button onClick={() => { setCurrentView('messages'); setShowMenu(false); }}>
              <MessageSquare size={18} /> Messages
              {currentEmployee && getEmployeeUnreadInternalCount(currentEmployee.id) > 0 && (
                <span className="menu-badge">{getEmployeeUnreadInternalCount(currentEmployee.id)}</span>
              )}
            </button>
            <button onClick={() => { setShowTimeOffRequest(true); setShowMenu(false); }}>
              <Calendar size={18} /> Request Time Off
            </button>
            <button onClick={() => { navigate('/onboarding'); setShowMenu(false); }}>
              <ClipboardList size={18} /> My Onboarding Checklist
              {onboardingOpenCount > 0 && (
                <span className="menu-badge">{onboardingOpenCount}</span>
              )}
            </button>
            <button onClick={() => { setShowHandbook(true); setShowMenu(false); }}>
              <Book size={18} /> Employee Handbook
            </button>
            <button onClick={() => { logout(); setShowMenu(false); }}>
              <LogOut size={18} /> Sign Out
            </button>
          </div>
        )}
      </header>

      {/* Clock In/Out Banner */}
      <div className={`clock-banner ${clockedIn ? 'clocked-in' : 'clocked-out'}`} onClick={() => setShowClockModal(true)}>
        {clockedIn ? (
          <>
            <div className="clock-status">
              <Clock size={20} />
              <div className="clock-info">
                <span className="clock-label">Clocked In{clockedInRole ? ` as ${clockedInRole.label}` : ''}</span>
                <span className="clock-time">Since {format(new Date(clockInTime), 'h:mm a')}</span>
              </div>
            </div>
            <div className="clock-hours">
              <span className="hours-worked">{getHoursWorked()}</span>
              <span className="hours-label">hrs today</span>
            </div>
          </>
        ) : (
          <>
            <div className="clock-status">
              <Play size={20} />
              <span className="clock-label">Tap to Clock In</span>
            </div>
            <ChevronRight size={20} />
          </>
        )}
      </div>

      {/* Date Banner */}
      <div className="date-banner">
        <div className="date-info">
          <Calendar size={18} />
          <span>{format(new Date(), 'EEEE, MMMM d, yyyy')}</span>
        </div>
        <div className={`gps-indicator ${gpsStatus}`}>
          {gpsStatus === 'active' ? <Wifi size={14} /> : <WifiOff size={14} />}
          <span>{gpsStatus === 'active' ? 'GPS Active' : gpsStatus === 'error' ? 'GPS Error' : 'Connecting...'}</span>
        </div>
      </div>

      {/* Pending Review Banner */}
      {currentEmployee && currentView !== 'review' && (() => {
        const pending = getPendingEmployeeReviews(currentEmployee.id);
        if (pending.length === 0) return null;
        return (
          <div className="review-banner" onClick={() => setCurrentView('review')}>
            <ClipboardList size={18} />
            <span>Your 90-Day Self-Assessment is ready — tap to complete</span>
            <ChevronRight size={16} />
          </div>
        );
      })()}

      {/* Onboarding Checklist Banner */}
      {onboardingOpenCount > 0 && (
        <div className="review-banner" onClick={() => navigate('/onboarding')}>
          <ClipboardList size={18} />
          <span>
            You have {onboardingOpenCount} onboarding {onboardingOpenCount === 1 ? 'item' : 'items'} to complete — tap to open your checklist
          </span>
          <ChevronRight size={16} />
        </div>
      )}

      {/* View Toggle Navigation */}
      <div className="view-toggle-nav">
        <button 
          className={`toggle-btn ${currentView === 'jobs' ? 'active' : ''}`}
          onClick={() => setCurrentView('jobs')}
        >
          <Calendar size={18} />
          Today's Jobs
        </button>
        <button
          className={`toggle-btn ${currentView === 'schedule' ? 'active' : ''}`}
          onClick={() => setCurrentView('schedule')}
        >
          <Clock size={18} />
          My Schedule
        </button>
        <button
          className={`toggle-btn ${currentView === 'myhours' ? 'active' : ''}`}
          onClick={() => setCurrentView('myhours')}
        >
          <Timer size={18} />
          My Hours
        </button>
        <button
          className={`toggle-btn ${currentView === 'messages' ? 'active' : ''}`}
          onClick={() => setCurrentView('messages')}
        >
          <MessageSquare size={18} />
          Messages
          {currentEmployee && getEmployeeUnreadInternalCount(currentEmployee.id) > 0 && (
            <span className="toggle-badge">{getEmployeeUnreadInternalCount(currentEmployee.id)}</span>
          )}
        </button>
        <button
          className={`toggle-btn ${currentView === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setCurrentView('leaderboard')}
        >
          <Trophy size={18} />
          Rankings
        </button>
        <button
          className={`toggle-btn ${currentView === 'quizzes' ? 'active' : ''}`}
          onClick={() => setCurrentView('quizzes')}
        >
          <Book size={18} />
          Quizzes
          {currentEmployee && (() => {
            const now = new Date();
            const monthAttempts = state.quizAttempts.filter(a => {
              if (a.employeeId !== currentEmployee.id || !a.passed) return false;
              const d = new Date(a.submittedAt);
              return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
            });
            const remaining = MONTHLY_QUIZ_REQUIREMENT - monthAttempts.length;
            return remaining > 0 ? <span className="toggle-badge">{remaining}</span> : null;
          })()}
        </button>
        <button
          className={`toggle-btn ${currentView === 'attendance' ? 'active' : ''}`}
          onClick={() => setCurrentView('attendance')}
        >
          <AlertTriangle size={18} />
          Attendance
        </button>
      </div>

      {/* Attendance View */}
      {currentView === 'attendance' && currentEmployee && (
        <EmployeeAttendanceView employeeId={currentEmployee.id} />
      )}

      {/* Review View */}
      {currentView === 'review' && currentEmployee && (() => {
        const pending = getPendingEmployeeReviews(currentEmployee.id);
        if (pending.length === 0) return <div style={{ padding: 20, textAlign: 'center' }}>No pending reviews.</div>;
        return (
          <EmployeeSelfReview
            review={pending[0]}
            employee={currentEmployee}
            onComplete={() => setCurrentView('jobs')}
          />
        );
      })()}

      {/* Messages View */}
      {currentView === 'messages' && currentEmployee && (
        <div className="messages-view-container">
          <EmployeeInbox 
            employeeId={currentEmployee.id} 
            onBack={() => setCurrentView('jobs')}
          />
        </div>
      )}

      {/* Schedule View */}
      {currentView === 'schedule' && currentEmployee && (
        <ScheduleView
          currentEmployee={currentEmployee}
          state={state}
          getEmployeeShifts={getEmployeeShifts}
          hasTimeOffOnDate={hasTimeOffOnDate}
        />
      )}

      {/* My Hours View */}
      {currentView === 'myhours' && currentEmployee && (
        <div className="my-hours-view">
          {/* Week Navigation */}
          <div className="my-hours-week-nav">
            <button className="my-hours-nav-btn" onClick={() => setMyHoursWeek(subWeeks(myHoursWeek, 1))}>
              <ChevronLeft size={20} />
            </button>
            <div className="my-hours-week-label">
              <Calendar size={16} />
              <span>{format(myHoursWeek, 'MMM d')} — {format(endOfWeek(myHoursWeek, { weekStartsOn: 1 }), 'MMM d, yyyy')}</span>
            </div>
            <button
              className="my-hours-nav-btn"
              onClick={() => setMyHoursWeek(addWeeks(myHoursWeek, 1))}
              disabled={myHoursWeek >= startOfWeek(new Date(), { weekStartsOn: 1 })}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Weekly Total */}
          <div className="my-hours-total-card">
            <div className="my-hours-total-row">
              <div className="my-hours-total-item">
                <Clock size={18} />
                <div>
                  <span className="my-hours-total-value">{myHoursData.totalHours}h</span>
                  <span className="my-hours-total-label">Total Hours</span>
                </div>
              </div>
            </div>
          </div>

          {/* Day-by-Day Breakdown */}
          {myHoursLoading ? (
            <div className="my-hours-loading">
              <Loader size={24} className="spin" />
              <span>Loading hours...</span>
            </div>
          ) : (
            <div className="my-hours-day-list">
              {myHoursData.days.map(day => (
                <div key={day.date} className={`my-hours-day-card ${day.isToday ? 'today' : ''} ${day.hrs === 0 ? 'empty' : ''} ${day.isClockedIn ? 'clocked-in' : ''}`}>
                  <div className="my-hours-day-header">
                    <div className="my-hours-day-info">
                      <span className="my-hours-day-name">{day.dayName}</span>
                      <span className="my-hours-day-date">{day.dayDate}</span>
                      {day.isToday && <span className="my-hours-today-badge">Today</span>}
                    </div>
                    <div className="my-hours-day-total">
                      {day.isClockedIn && <span className="my-hours-live-dot" />}
                      <span className="my-hours-day-hrs">{day.hrs > 0 ? `${day.hrs}h` : '—'}</span>
                    </div>
                  </div>
                  {day.hrs > 0 && (
                    <div className="my-hours-day-details">
                      {day.clockIn && (
                        <div className="my-hours-time-row">
                          <Play size={12} />
                          <span>In: {(() => { try { return format(new Date(day.clockIn), 'h:mm a'); } catch { return '—'; } })()}</span>
                        </div>
                      )}
                      {day.clockOut && (
                        <div className="my-hours-time-row">
                          <CheckCircle size={12} />
                          <span>Out: {(() => { try { return format(new Date(day.clockOut), 'h:mm a'); } catch { return '—'; } })()}</span>
                        </div>
                      )}
                      {day.isClockedIn && !day.clockOut && (
                        <div className="my-hours-time-row live">
                          <Timer size={12} />
                          <span>Currently clocked in</span>
                        </div>
                      )}
                      {day.role && (
                        <div className="my-hours-time-row">
                          <Tag size={12} />
                          <span>{day.role}{day.rate ? ` — $${day.rate}/hr` : ''}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leaderboard View */}
      {currentView === 'leaderboard' && currentEmployee && (() => {
        const data = lbView === 'week' ? leaderboard.week : leaderboard.month;
        const myRank = data.findIndex(e => e.empId === currentEmployee.id) + 1;
        const label = lbView === 'week' ? leaderboard.weekLabel : leaderboard.monthLabel;
        
        return (
          <div className="ta-leaderboard-view">
            {/* Toggle */}
            <div className="ta-lb-toggle">
              <button className={lbView === 'week' ? 'active' : ''} onClick={() => setLbView('week')}>This Week</button>
              <button className={lbView === 'month' ? 'active' : ''} onClick={() => setLbView('month')}>This Month</button>
            </div>
            <div className="ta-lb-period">{label}</div>

            {/* My rank highlight */}
            {myRank > 0 && (
              <div className="ta-lb-my-rank">
                <div className="ta-lb-my-rank-icon">
                  {myRank === 1 ? <Trophy size={28} /> : myRank === 2 ? <Award size={28} /> : myRank === 3 ? <Medal size={28} /> : <span className="ta-lb-rank-num">#{myRank}</span>}
                </div>
                <div className="ta-lb-my-rank-text">
                  <span className="ta-lb-my-rank-label">Your Rank</span>
                  <span className="ta-lb-my-rank-value">
                    {myRank === 1 ? '1st Place 🏆' : myRank === 2 ? '2nd Place 🥈' : myRank === 3 ? '3rd Place 🥉' : `#${myRank} of ${data.length}`}
                  </span>
                  <span className="ta-lb-my-rank-jobs">{data[myRank - 1]?.daysWorked || 0}d · {data[myRank - 1]?.routeCount || 0} route{(data[myRank - 1]?.routeCount || 0) !== 1 ? 's' : ''}</span>
                </div>
              </div>
            )}

            {/* Full rankings */}
            <div className="ta-lb-list">
              {data.length === 0 && <div className="ta-lb-empty">No completed routes for this period yet.</div>}
              {data.map((person, idx) => {
                const isMe = person.empId === currentEmployee.id;
                const maxProfit = data[0]?.totalProfit || 1;
                const barPct = maxProfit > 0 ? Math.max(5, (person.totalProfit / maxProfit) * 100) : 5;
                return (
                  <div key={person.empId} className={`ta-lb-row ${isMe ? 'me' : ''}`}>
                    <div className={`ta-lb-rank rank-${Math.min(idx, 3)}`}>
                      {idx === 0 ? <Trophy size={18} /> : idx === 1 ? <Award size={18} /> : idx === 2 ? <Medal size={18} /> : idx + 1}
                    </div>
                    <div className="ta-lb-info">
                      <span className="ta-lb-name">{person.name}{isMe ? ' (You)' : ''}</span>
                      <span className="ta-lb-meta">{person.daysWorked}d · {person.routeCount} route{person.routeCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="ta-lb-bar-wrap">
                      <div className={`ta-lb-bar ${person.totalProfit < 0 ? 'negative' : ''}`} style={{ width: `${person.totalProfit > 0 ? barPct : 5}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Quizzes View */}
      {currentView === 'quizzes' && currentEmployee && (() => {
        const now = new Date();
        const allQuizzes = (() => {
          const firestoreIds = new Set(state.quizzes.map(q => q.id));
          const merged = [...state.quizzes];
          quizBank.forEach(q => { if (!firestoreIds.has(q.id)) merged.push(q); });
          return merged.filter(q => q.status === 'active');
        })();
        const monthPassed = state.quizAttempts.filter(a => {
          if (a.employeeId !== currentEmployee.id || !a.passed) return false;
          const d = new Date(a.submittedAt);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        });
        const remaining = MONTHLY_QUIZ_REQUIREMENT - monthPassed.length;
        const myAttempts = getEmployeeQuizAttempts(currentEmployee.id);

        if (activeQuiz && !quizSubmitted) {
          const quiz = allQuizzes.find(q => q.id === activeQuiz);
          if (!quiz) return <div style={{ padding: 20, textAlign: 'center' }}>Quiz not found.</div>;
          const allAnswered = quiz.questions.every(q => quizAnswers[q.id] !== undefined);
          return (
            <div className="ta-quiz-take">
              <div className="ta-quiz-take-header">
                <button className="ta-quiz-back-btn" onClick={() => { setActiveQuiz(null); setQuizAnswers({}); }}>
                  <ChevronLeft size={18} /> Back
                </button>
                <h3>{quiz.title}</h3>
                <span className="ta-quiz-progress">{Object.keys(quizAnswers).length}/{quiz.questions.length}</span>
              </div>
              <div className="ta-quiz-questions">
                {quiz.questions.map((q, qi) => (
                  <div key={q.id} className={`ta-quiz-question ${quizAnswers[q.id] !== undefined ? 'answered' : ''}`}>
                    <div className="ta-quiz-q-num">Q{qi + 1}</div>
                    <p className="ta-quiz-q-text">{q.text}</p>
                    <div className="ta-quiz-options">
                      {q.options.map((opt, oi) => (
                        <button
                          key={oi}
                          className={`ta-quiz-option ${quizAnswers[q.id] === oi ? 'selected' : ''}`}
                          onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: oi }))}
                        >
                          <span className="ta-quiz-option-letter">{String.fromCharCode(65 + oi)}</span>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button
                className="ta-quiz-submit-btn"
                disabled={!allAnswered}
                onClick={async () => {
                  let correct = 0;
                  quiz.questions.forEach(q => {
                    if (quizAnswers[q.id] === q.correctIndex) correct++;
                  });
                  const score = Math.round((correct / quiz.questions.length) * 100);
                  const passed = score >= quiz.passingScore;
                  const result = { score, correct, total: quiz.questions.length, passed, answers: quizAnswers };
                  setQuizResult(result);
                  setQuizSubmitted(true);
                  await submitQuizAttempt({
                    quizId: quiz.id,
                    quizTitle: quiz.title,
                    employeeId: currentEmployee.id,
                    employeeName: `${currentEmployee.firstName} ${currentEmployee.lastName}`,
                    score,
                    passed,
                    correctCount: correct,
                    totalQuestions: quiz.questions.length,
                    answers: quizAnswers
                  });
                }}
              >
                Submit Quiz
              </button>
            </div>
          );
        }

        if (quizSubmitted && quizResult) {
          const quiz = allQuizzes.find(q => q.id === activeQuiz);
          return (
            <div className="ta-quiz-result">
              <div className={`ta-quiz-result-card ${quizResult.passed ? 'pass' : 'fail'}`}>
                {quizResult.passed ? <CheckCircle size={48} /> : <AlertTriangle size={48} />}
                <h3>{quizResult.passed ? 'Great Job!' : 'Keep Studying'}</h3>
                <div className="ta-quiz-result-score">{quizResult.score}%</div>
                <p>{quizResult.correct} of {quizResult.total} correct</p>
                <p className="ta-quiz-result-pass-note">
                  {quizResult.passed ? 'You passed this quiz!' : `You need ${quiz?.passingScore || 70}% to pass. Review the answers below and try again.`}
                </p>
              </div>
              {quiz && (
                <div className="ta-quiz-review">
                  <h4>Review Answers</h4>
                  {quiz.questions.map((q, qi) => {
                    const userAnswer = quizResult.answers[q.id];
                    const isCorrect = userAnswer === q.correctIndex;
                    return (
                      <div key={q.id} className={`ta-quiz-review-item ${isCorrect ? 'correct' : 'wrong'}`}>
                        <div className="ta-quiz-review-q">
                          <span className="ta-quiz-review-icon">{isCorrect ? <CheckCircle size={16} /> : <X size={16} />}</span>
                          <strong>Q{qi + 1}:</strong> {q.text}
                        </div>
                        {!isCorrect && (
                          <div className="ta-quiz-review-answer">
                            <span className="ta-quiz-review-yours">Your answer: {q.options[userAnswer]}</span>
                            <span className="ta-quiz-review-correct">Correct: {q.options[q.correctIndex]}</span>
                          </div>
                        )}
                        <p className="ta-quiz-review-explanation">{q.explanation}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                className="ta-quiz-done-btn"
                onClick={() => { setActiveQuiz(null); setQuizAnswers({}); setQuizSubmitted(false); setQuizResult(null); }}
              >
                Back to Quizzes
              </button>
            </div>
          );
        }

        return (
          <div className="ta-quiz-list">
            {/* Monthly progress */}
            <div className="ta-quiz-progress-card">
              <div className="ta-quiz-progress-bar-wrap">
                <div className="ta-quiz-progress-bar" style={{ width: `${Math.min(100, (monthPassed.length / MONTHLY_QUIZ_REQUIREMENT) * 100)}%` }} />
              </div>
              <div className="ta-quiz-progress-text">
                <strong>{monthPassed.length}/{MONTHLY_QUIZ_REQUIREMENT}</strong> quizzes passed this month
                {remaining > 0 ? (
                  <span className="ta-quiz-remaining"> — {remaining} more needed</span>
                ) : (
                  <span className="ta-quiz-complete"> — Requirement met!</span>
                )}
              </div>
            </div>

            {/* Available quizzes */}
            <h3 style={{ margin: '0 16px', fontSize: '16px', color: '#1a2634' }}>Available Quizzes</h3>
            {allQuizzes.map(quiz => {
              const lastAttempt = myAttempts.find(a => a.quizId === quiz.id);
              const passedThisMonth = monthPassed.some(a => a.quizId === quiz.id);
              return (
                <div key={quiz.id} className="ta-quiz-item">
                  <div className="ta-quiz-item-top">
                    <div className="ta-quiz-item-info">
                      <span className="ta-quiz-item-title">{quiz.title}</span>
                      <span className="ta-quiz-item-meta">{quiz.questions.length} questions · {quiz.timeEstimate} · {quiz.passingScore}% to pass</span>
                    </div>
                    {passedThisMonth ? (
                      <span className="ta-quiz-item-done"><CheckCircle size={16} /> Done</span>
                    ) : (
                      <button
                        className="ta-quiz-start-btn"
                        onClick={() => { setActiveQuiz(quiz.id); setQuizAnswers({}); setQuizSubmitted(false); setQuizResult(null); }}
                      >
                        {lastAttempt ? 'Retake' : 'Start'}
                      </button>
                    )}
                  </div>
                  <p className="ta-quiz-item-desc">{quiz.description}</p>
                  {lastAttempt && (
                    <div className="ta-quiz-item-last">
                      Last attempt: {lastAttempt.score}% {lastAttempt.passed ? '(Passed)' : '(Not passed)'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Jobs View */}
      {currentView === 'jobs' && (
        <>
      {/* Stats Summary */}
      <div className="stats-summary">
        <div className="stat-item">
          <span className="stat-number">{stats.totalJobs}</span>
          <span className="stat-label">Jobs</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.completed}</span>
          <span className="stat-label">Done</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.totalHours}h</span>
          <span className="stat-label">Hours</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.inProgress > 0 ? stats.inProgress : stats.totalJobs - stats.completed}</span>
          <span className="stat-label">Remaining</span>
        </div>
      </div>

      {/* GPS Status */}
      {locationError && (
        <div className="gps-error">
          <AlertTriangle size={16} />
          <span>GPS unavailable: {locationError}</span>
        </div>
      )}

      {/* My Team Today */}
      {myTeammates.length > 0 && (
        <div className="team-roster-card">
          <div className="team-roster-header">
            <User size={16} />
            <span>Your Team Today</span>
          </div>
          <div className="team-roster-list">
            {myTeammates.map(mate => (
              <div key={mate.id} className="team-roster-member">
                <div className="team-roster-avatar">
                  {mate.firstName?.[0] || '?'}{mate.lastName?.[0] || ''}
                </div>
                <span className="team-roster-name">{mate.firstName} {mate.lastName}</span>
                {mate.phone && (
                  <a href={`tel:${mate.phone}`} className="team-roster-phone">
                    <Phone size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Hours */}
      <div className="weekly-hours-card">
        <div className="weekly-hours-header">
          <div className="weekly-hours-title">
            <Clock size={16} />
            <span>Clock Hours This Week</span>
          </div>
          <span className="weekly-hours-total">{weeklyHours.total}h total</span>
        </div>
        <div className="weekly-hours-bars">
          {weeklyHours.days.map(d => (
            <div key={d.date} className={`weekly-hours-day ${d.isToday ? 'today' : ''} ${d.hrs === 0 ? 'empty' : ''}`}>
              <div className="weekly-hours-bar-wrap">
                <div
                  className={`weekly-hours-bar-fill ${d.clockedIn ? 'live' : ''}`}
                  style={{ height: `${Math.min((d.hrs / 10) * 100, 100)}%` }}
                />
              </div>
              <span className="weekly-hours-hrs">{d.hrs > 0 ? `${d.hrs}` : '—'}</span>
              <span className="weekly-hours-label">{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Jobs List */}
      <div className="jobs-section">
        <h2>Today's Route</h2>
        
        {myJobs.length === 0 ? (
          <div className="no-jobs">
            <Home size={48} />
            <p>No jobs scheduled for today</p>
          </div>
        ) : (
          <div className="jobs-list">
            {myJobs.map((job, index) => {
              const isActive = job.status === 'in_progress';
              
              return (
                <div 
                  key={job.id} 
                  className={`job-card-simple ${isActive ? 'active' : ''} ${job.status}`}
                  onClick={() => setExpandedJob(job.id)}
                >
                  <div className="job-number">{index + 1}</div>
                  <div className="job-info">
                    <div className="job-time-status">
                      <span className="time">{formatTime(job.scheduledTime)}</span>
                      <span 
                        className="status-badge"
                        style={{ background: getStatusColor(job.status) }}
                      >
                        {getStatusLabel(job.status)}
                      </span>
                    </div>
                    <h3 className="client-name">{job.clientName}</h3>
                    <p className="address">{job.address}</p>
                    <div className="job-meta-row">
                      <span className="job-type-badge" style={{ color: serviceTypes[job.serviceType]?.color || '#6b7280' }}>
                        {serviceTypes[job.serviceType]?.icon} {serviceTypes[job.serviceType]?.label || job.serviceType}
                      </span>
                      {job.duration && <span className="job-duration-badge">{job.duration}h</span>}
                    </div>
                    {job.notes && (
                      <p className="job-notes-preview">{job.notes.length > 60 ? job.notes.slice(0, 60) + '…' : job.notes}</p>
                    )}
                    {job.team && job.team.length > 1 && (
                      <div className="job-team-row">
                        <User size={12} />
                        <span>
                          {(job.teamNames || job.team.map(id => {
                            const e = state.employees.find(emp => emp.id === id);
                            return e ? e.firstName : id;
                          })).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <ChevronRight size={20} className="chevron" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {expandedJob && (() => {
        const job = myJobs.find(j => j.id === expandedJob);
        if (!job) return null;
        
        return (
          <div className="job-modal-overlay" onClick={() => setExpandedJob(null)}>
            <div className="job-modal" onClick={e => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="modal-header">
                <button className="back-btn" onClick={() => setExpandedJob(null)}>
                  <ChevronLeft size={24} />
                </button>
                <h2>Job Details</h2>
                <span 
                  className="status-pill"
                  style={{ background: getStatusColor(job.status) }}
                >
                  {getStatusLabel(job.status)}
                </span>
              </div>

              {/* Modal Content */}
              <div className="modal-body">
                {/* Client & Time */}
                <div className="detail-section hero">
                  <h1 className="client-name">{job.clientName}</h1>
                  <p className="time-range">
                    <Clock size={18} />
                    {formatTime(job.scheduledTime)} - {formatTime(job.scheduledEndTime)}
                  </p>
                  <p className="address-line">
                    <MapPin size={18} />
                    {job.address}, {job.city || 'Charlottesville'}, {job.state || 'VA'}
                  </p>
                </div>

                {/* Property Photo — show existing or upload button */}
                {(() => {
                  const property = job.propertyId ? getPropertyById(job.propertyId) : null;
                  const photoUrl = property?.photoUrl || job.propertyPhotoUrl;
                  return (
                    <div className="detail-section property-photo-section">
                      {photoUrl ? (
                        <div className="property-photo-preview">
                          <img src={photoUrl} alt="Property" className="property-photo-img" />
                          <button 
                            className="property-photo-retake"
                            onClick={() => propertyPhotoRef.current?.click()}
                            disabled={uploadingPropertyPhoto}
                          >
                            <Camera size={14} /> {uploadingPropertyPhoto ? 'Uploading...' : 'Retake'}
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="property-photo-upload-btn"
                          onClick={() => propertyPhotoRef.current?.click()}
                          disabled={uploadingPropertyPhoto}
                        >
                          <Home size={20} />
                          <span>{uploadingPropertyPhoto ? 'Uploading...' : '📷 Take Property Photo'}</span>
                          <span className="property-photo-hint">Photo of the outside of the home</span>
                        </button>
                      )}
                      <input
                        ref={propertyPhotoRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={(e) => handlePropertyPhotoUpload(e, job)}
                        disabled={uploadingPropertyPhoto}
                      />
                    </div>
                  );
                })()}

                {/* Quick Stats */}
                <div className="detail-section stats-row">
                  <div className="stat">
                    <Timer size={20} />
                    <span>{job.duration} hrs</span>
                  </div>
                  <div className="stat">
                    <Tag size={20} />
                    <span>{serviceTypes[job.serviceType]?.label || job.serviceType}</span>
                  </div>
                  {job.sqft > 0 && (
                    <div className="stat">
                      <Ruler size={20} />
                      <span>{Number(job.sqft).toLocaleString()} sqft</span>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                {(job.bedrooms || job.bathrooms) && (
                  <div className="detail-section property">
                    <h4>Property</h4>
                    <div className="property-row">
                      {job.bedrooms > 0 && (
                        <span><BedDouble size={16} /> {job.bedrooms} Beds</span>
                      )}
                      {job.bathrooms > 0 && (
                        <span><Bath size={16} /> {job.bathrooms} Baths</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Pets */}
                {job.pets && (
                  <div className="detail-section pets">
                    <h4><PawPrint size={16} /> Pets</h4>
                    <p>{typeof job.pets === 'string' ? job.pets : 'Has pets'}</p>
                    {job.petDetails && <p className="pet-details">{job.petDetails}</p>}
                  </div>
                )}

                {/* Access Instructions */}
                {(job.accessInstructions || job.accessInfo) && (
                  <div className="detail-section access">
                    <h4><Key size={16} /> Access Instructions</h4>
                    <p>{String(job.accessInstructions || job.accessInfo)}</p>
                  </div>
                )}

                {/* Add-Ons */}
                {job.selectedAddons && Array.isArray(job.selectedAddons) && job.selectedAddons.length > 0 && (
                  <div className="detail-section addons">
                    <h4><Sparkles size={16} /> Add-On Services</h4>
                    <ul>
                      {job.selectedAddons.map((addon, i) => (
                        <li key={i}>{typeof addon === 'string' ? addon : addon?.name || 'Add-on'}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Job Notes */}
                {(job.notes || job.clientNotes || job.specialInstructions) && (
                  <div className="detail-section notes">
                    <h4><FileText size={16} /> Notes</h4>
                    {job.notes && <p>{job.notes}</p>}
                    {job.clientNotes && <p>{job.clientNotes}</p>}
                    {job.specialInstructions && (
                      <p className="special"><AlertTriangle size={14} /> {job.specialInstructions}</p>
                    )}
                  </div>
                )}

                {/* Custom Cleaning Checklist (per-client extras beyond standard scope) */}
                {(() => {
                  const client = state.clients.find(c => c.id === job.clientId);
                  const allItems = client?.customChecklist?.items || [];
                  const clientJobs = state.jobs.filter(j => j.clientId === job.clientId);
                  const items = getApplicableChecklistItems(allItems, job, clientJobs);
                  if (items.length === 0) return null;
                  const completion = job.checklistCompletion || {};
                  const doneCount = items.filter(i => completion[i.id]?.completed).length;
                  return (
                    <div className="detail-section checklist">
                      <h4>
                        <ClipboardList size={16} /> Custom Checklist
                        <span className="checklist-progress">{doneCount} of {items.length} done</span>
                      </h4>
                      <ul className="job-checklist">
                        {items.map(item => {
                          const done = !!completion[item.id]?.completed;
                          const freq = item.frequency || 'daily';
                          const freqLabel = CHECKLIST_FREQUENCY_LABELS[freq];
                          return (
                            <li
                              key={item.id}
                              className={`job-checklist-item ${done ? 'done' : ''}`}
                              onClick={() => updateJobChecklistItem(job.id, item.id, !done, currentEmployee?.id)}
                            >
                              <span className="job-checklist-box">
                                {done ? <CheckCircle size={20} /> : <Square size={20} />}
                              </span>
                              <span className="job-checklist-text">{item.text}</span>
                              {freq !== 'daily' && freqLabel && (
                                <span className="job-checklist-freq">{freqLabel}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()}

                {/* Client Phone */}
                {job.clientPhone && (
                  <a href={`tel:${job.clientPhone}`} className="phone-button">
                    <Phone size={20} />
                    <span>Call Client: {job.clientPhone}</span>
                  </a>
                )}
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                {job.status === 'scheduled' && (
                  <>
                    <button 
                      className="action-btn navigate"
                      onClick={() => openNavigation(job.fullAddress || `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`)}
                    >
                      <Navigation size={20} />
                      Navigate
                    </button>
                    <button 
                      className="action-btn primary"
                      onClick={() => { handleOnMyWay(job); setExpandedJob(null); }}
                    >
                      <Car size={20} />
                      On My Way
                    </button>
                  </>
                )}

                {job.status === 'en_route' && (
                  <>
                    <button 
                      className="action-btn navigate"
                      onClick={() => openNavigation(job.fullAddress || `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`)}
                    >
                      <Navigation size={20} />
                      Navigate
                    </button>
                    <button 
                      className="action-btn primary"
                      onClick={() => { handleStartJob(job); setExpandedJob(null); }}
                    >
                      <Play size={20} />
                      Start Job
                    </button>
                  </>
                )}

                {job.status === 'in_progress' && (
                  <>
                    <button 
                      className="action-btn secondary"
                      onClick={() => { setPhotoJobId(job.id); setPhotoType('before'); setShowPhotoModal(true); }}
                    >
                      <Camera size={20} />
                      Photos
                    </button>
                    <button 
                      className="action-btn warning"
                      onClick={() => { setPhotoJobId(job.id); setShowDamageModal(true); }}
                    >
                      <AlertTriangle size={20} />
                      Report Issue
                    </button>
                    <button 
                      className="action-btn success full-width"
                      onClick={() => { handleCompleteJob(job); setExpandedJob(null); }}
                    >
                      <CheckCircle size={20} />
                      Complete Job
                    </button>
                  </>
                )}

                {job.status === 'completed' && (
                  <div className="completed-banner">
                    <CheckCircle size={24} />
                    <div>
                      <strong>Completed</strong>
                      <span>{format(new Date(job.completedAt || job.actual_end), 'h:mm a')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
        </>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => !isUploading && setShowPhotoModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload {photoType === 'before' ? 'Before' : 'After'} Photos</h3>
              <button className="close-btn" onClick={() => !isUploading && setShowPhotoModal(false)} disabled={isUploading}>
                <X size={24} />
              </button>
            </div>
            <div className="photo-type-selector">
              <button 
                className={photoType === 'before' ? 'active' : ''}
                onClick={() => setPhotoType('before')}
                disabled={isUploading}
              >
                Before
              </button>
              <button 
                className={photoType === 'after' ? 'active' : ''}
                onClick={() => setPhotoType('after')}
                disabled={isUploading}
              >
                After
              </button>
            </div>
            
            {/* Selected photos preview */}
            {selectedPhotos.length > 0 && (
              <div className="selected-photos-preview">
                {selectedPhotos.map((file, index) => (
                  <div key={index} className="photo-preview-item">
                    <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} />
                    <button 
                      className="remove-photo-btn" 
                      onClick={() => removeSelectedPhoto(index)}
                      disabled={isUploading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div 
              className="photo-upload-area"
              onClick={() => !isUploading && photoInputRef.current?.click()}
            >
              <Camera size={48} />
              <p>{selectedPhotos.length > 0 ? 'Tap to add more' : `Tap to take ${photoType} photos`}</p>
              <input 
                ref={photoInputRef}
                type="file" 
                accept="image/*" 
                
                multiple 
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
            </div>
            
            {uploadProgress && (
              <div className="upload-progress">
                <Loader size={18} className="spinning" />
                <span>{uploadProgress}</span>
              </div>
            )}
            
            <button 
              className="upload-btn" 
              onClick={handlePhotoUpload}
              disabled={selectedPhotos.length === 0 || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader size={18} className="spinning" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Upload {selectedPhotos.length} Photo{selectedPhotos.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Damage Report Modal */}
      {showDamageModal && (
        <div className="modal-overlay" onClick={() => !isUploading && setShowDamageModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Report Issue</h3>
              <button className="close-btn" onClick={() => !isUploading && setShowDamageModal(false)} disabled={isUploading}>
                <X size={24} />
              </button>
            </div>
            <div className="damage-type-selector">
              <button 
                className={damageType === 'existing' ? 'active' : ''}
                onClick={() => setDamageType('existing')}
                disabled={isUploading}
              >
                <Info size={18} />
                Pre-existing
              </button>
              <button 
                className={damageType === 'new' ? 'active' : ''}
                onClick={() => setDamageType('new')}
                disabled={isUploading}
              >
                <AlertTriangle size={18} />
                New Damage
              </button>
            </div>
            
            {/* Damage photos preview */}
            {damagePhotos.length > 0 && (
              <div className="selected-photos-preview">
                {damagePhotos.map((file, index) => (
                  <div key={index} className="photo-preview-item">
                    <img src={URL.createObjectURL(file)} alt={`Damage ${index + 1}`} />
                    <button 
                      className="remove-photo-btn" 
                      onClick={() => removeDamagePhoto(index)}
                      disabled={isUploading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div 
              className="photo-upload-area small"
              onClick={() => !isUploading && damagePhotoInputRef.current?.click()}
            >
              <Camera size={32} />
              <p>{damagePhotos.length > 0 ? `${damagePhotos.length} photo(s) - tap to add more` : 'Photo required - tap to capture'}</p>
              <input 
                ref={damagePhotoInputRef}
                type="file" 
                accept="image/*" 
                
                multiple
                onChange={handleDamagePhotoSelect}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
            </div>
            
            <textarea
              placeholder="Describe the issue..."
              value={damageDescription}
              onChange={(e) => setDamageDescription(e.target.value)}
              rows={4}
              disabled={isUploading}
            />
            
            {uploadProgress && (
              <div className="upload-progress">
                <Loader size={18} className="spinning" />
                <span>{uploadProgress}</span>
              </div>
            )}
            
            <button 
              className="submit-btn" 
              onClick={handleReportDamage}
              disabled={!damageDescription.trim() || damagePhotos.length === 0 || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader size={18} className="spinning" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Submit Report
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Clock In/Out Modal */}
      {showClockModal && (
        <div className="modal-overlay" onClick={() => setShowClockModal(false)}>
          <div className="modal-content clock-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{clockedIn ? 'Clock Out' : 'Clock In'}</h3>
              <button className="close-btn" onClick={() => setShowClockModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="clock-modal-content">
              <div className="clock-employee">
                <div className="clock-avatar">
                  {currentEmployee?.firstName?.[0]}{currentEmployee?.lastName?.[0]}
                </div>
                <div className="clock-employee-info">
                  <span className="employee-name">{currentEmployee?.firstName} {currentEmployee?.lastName}</span>
                  <span className="employee-role">
                    {clockedInRole ? clockedInRole.label : currentEmployee?.role === 'lead' ? 'Team Lead' : 'Cleaner'}
                  </span>
                </div>
              </div>
              
              <div className="clock-time-display">
                <span className="current-time">{format(new Date(), 'h:mm a')}</span>
                <span className="current-date">{format(new Date(), 'EEEE, MMMM d')}</span>
              </div>
              
              {clockedIn && (
                <div className="clock-summary">
                  <div className="summary-item">
                    <span className="summary-label">Clocked In At</span>
                    <span className="summary-value">{format(new Date(clockInTime), 'h:mm a')}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Hours Worked</span>
                    <span className="summary-value">{getHoursWorked()}</span>
                  </div>
                </div>
              )}

              {currentLocation && (
                <div className="clock-location">
                  <MapPin size={14} />
                  <span>Location will be recorded</span>
                </div>
              )}
            </div>
            
            {!clockedIn && currentEmployee?.payRates?.length > 0 && (
              <div className="pay-rate-selection" style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1a2634', marginBottom: '8px' }}>What are you clocking in as?</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {currentEmployee.payRates.map((pr, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedPayRate(pr)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                        border: selectedPayRate?.label === pr.label ? '2px solid #1a2634' : '2px solid #e2e8f0',
                        background: selectedPayRate?.label === pr.label ? '#f0f4f8' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: '15px' }}>{pr.label}</span>
                      <span style={{ fontWeight: 600, color: '#1a2634', fontSize: '15px' }}>${pr.rate.toFixed(2)}/hr</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {clockedIn ? (
              <button className="clock-out-btn" onClick={handleDailyClockOut}>
                <CheckCircle size={20} />
                Clock Out
              </button>
            ) : (
              <button
                className="clock-in-btn"
                onClick={handleDailyClockIn}
                disabled={currentEmployee?.payRates?.length > 0 && !selectedPayRate}
                style={currentEmployee?.payRates?.length > 0 && !selectedPayRate ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <Play size={20} />
                Clock In{selectedPayRate ? ` as ${selectedPayRate.label}` : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Laundry Pickup Modal */}
      {showLaundryModal && (
        <div className="modal-overlay" onClick={() => setShowLaundryModal(false)}>
          <div className="modal-content laundry-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Laundry Pickup Details</h3>
              <button className="close-btn" onClick={() => setShowLaundryModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="laundry-form">
              <div className="photo-upload-area">
                <Camera size={32} />
                <p>Take photo of bags</p>
                <input type="file" accept="image/*" />
              </div>
              
              <div className="form-group">
                <label>Weight (lbs) *</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Enter weight in pounds"
                  value={laundryData.weight}
                  onChange={(e) => setLaundryData({...laundryData, weight: e.target.value})}
                />
                {laundryData.weight && (
                  <span className="price-preview">
                    Charge: ${(parseFloat(laundryData.weight) * 3).toFixed(2)}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label>Number of Bags</label>
                <input
                  type="number"
                  min="1"
                  value={laundryData.bagCount}
                  onChange={(e) => setLaundryData({...laundryData, bagCount: parseInt(e.target.value) || 1})}
                />
              </div>
              
              <div className="form-group">
                <label>Bag Description</label>
                <textarea
                  placeholder="Describe the bags (colors, type, etc.)"
                  value={laundryData.bagDescription}
                  onChange={(e) => setLaundryData({...laundryData, bagDescription: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="form-group">
                <label>Bag Tags</label>
                <input
                  type="text"
                  placeholder="Tag numbers/labels on bags"
                  value={laundryData.bagTags}
                  onChange={(e) => setLaundryData({...laundryData, bagTags: e.target.value})}
                />
              </div>
              
              <button 
                className="submit-btn"
                onClick={handleCompleteLaundryJob}
                disabled={!laundryData.weight}
              >
                <CheckCircle size={18} />
                Complete Pickup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Handbook Modal */}
      {showHandbook && (
        <div className="handbook-modal">
          <div className="handbook-header">
            <h2><Book size={20} /> Employee Handbook</h2>
            <button className="close-btn" onClick={() => setShowHandbook(false)}>
              <X size={24} />
            </button>
          </div>
          <iframe 
            src="/handbook.html" 
            title="Employee Handbook"
            className="handbook-iframe"
          />
        </div>
      )}

      {/* Time Off Request Modal */}
      {currentEmployee && (
        <TimeOffRequest
          isOpen={showTimeOffRequest}
          onClose={() => setShowTimeOffRequest(false)}
          employeeId={currentEmployee.id}
        />
      )}
    </div>
  );
}

export default TeamApp;
